import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRef } from 'react';

import { CompetitionStatusEnum } from '@flipster/apis/v1/tradingCompetition/types';
import { FormattedMessage } from '@flipster/i18n';
import { Badge } from '@flipster/react/design-system/badge';
import { Datetime } from '@flipster/react/design-utils/datetime';
import { observeIntersectionOnce, useIntersection } from '@flipster/react/misc/browser/intersection';
import { useBreakpoint } from '@flipster/react/misc/responsive/index';
import { css } from '@flipster/styled-system/css';
import { center, stack } from '@flipster/styled-system/patterns';
import { isSameUTCYear } from '@flipster/utils/datetime';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';

import { competitionBunja } from './bunja';

export const Hero = () => {
  const { competitionAtom } = useBunja(competitionBunja);
  const competition = useAtomValue(competitionAtom);
  const heroRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useBreakpoint();

  useIntersection(
    heroRef,
    observeIntersectionOnce(() => {
      segment.track(CompetitionsEvent.CompetitionLandingViewPageScroll, {
        section_title: 'Banner',
      });
    }),
  );
  if (!competition) return null;

  const isVideoBanner = competition.bannerUrls.desktop.includes('.mp4');

  return (
    <div
      className={stack({
        tablet: {
          maxW: 'desktopMaxWidth',
          boxSizing: 'content-box',
        },
        mx: 'auto',
        px: 16,
        py: { base: 24, tablet: 60 },
        bg: 'gray.black',
        flexDirection: { base: 'column', tablet: 'row' },
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 40,
      })}
      ref={heroRef}
    >
      <div
        className={stack({
          gap: 8,
          textAlign: { base: 'center', tablet: 'left' },
          alignItems: { base: 'center', tablet: 'flex-start' },
        })}
      >
        {competition.competitionStatus === CompetitionStatusEnum.Finished && (
          <Badge type='tint' color='neutral' css={{ w: 'fit-content' }}>
            <FormattedMessage id='Autogen_common.r251126.s_Closed' />
          </Badge>
        )}
        <p className={css({ textStyle: 'Display/XL', color: 'inverse.white' })}>{competition.title}</p>
        <div className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-2' })}>
          <Datetime value={competition.registrationStartTime} format='LLL d, yyyy, HH:mm' isUTC />
          <span> - </span>
          <Datetime
            value={competition.registrationEndTime}
            format={
              isSameUTCYear(competition.registrationStartTime, competition.registrationEndTime)
                ? 'LLL d, HH:mm'
                : 'LLL d, yyyy, HH:mm'
            }
            isUTC
            withUTCText
          />
        </div>
      </div>
      <section
        className={center({
          overflow: 'hidden',
          '& :is(img, video)': {
            w: {
              base: 150,
              tablet: 320,
            },
            h: {
              base: 150,
              tablet: 320,
            },
          },
          flexShrink: 0,
        })}
      >
        {isVideoBanner ? (
          <video
            playsInline
            autoPlay
            muted
            loop
            src={isMobile ? competition.bannerUrls.mobile : competition.bannerUrls.desktop}
          >
            <track kind='captions' />
          </video>
        ) : (
          <img
            src={isMobile ? competition.bannerUrls.mobile : competition.bannerUrls.desktop}
            alt='trading-competition-banner'
          />
        )}
      </section>
    </div>
  );
};
